import { useState } from 'react';
import { Icon, Text, tokens } from '@/design';
import {
  applyLineSymbolPreset,
  LINE_SYMBOL_PRESETS,
  lineSymbolSvgDataUrl,
  type LineSymbolState,
} from './lineSymbolRegistry';

export interface LineSymbolPickerProps {
  readonly value: LineSymbolState;
  readonly readOnly: boolean;
  readonly compact?: boolean;
  readonly onChange: (next: LineSymbolState) => void;
  readonly 'data-testid'?: string;
}

export function LineSymbolPicker({
  value,
  readOnly,
  compact = false,
  onChange,
  'data-testid': testId = 'line-symbol-picker',
}: LineSymbolPickerProps) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={testId}
      style={{
        position: 'relative',
        display: compact ? 'inline-flex' : 'flex',
        alignItems: 'center',
        gap: tokens.space[2],
        minWidth: 0,
      }}
    >
      {compact ? null : <Text variant="caption" as="span">Symbol</Text>}
      <button
        type="button"
        aria-label="Line symbol"
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={readOnly}
        onClick={() => setOpen((current) => !current)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: tokens.space[1],
          minHeight: tokens.density.touchMin,
          padding: tokens.space[1],
          border: tokens.border.thin,
          borderRadius: tokens.radius.sm,
          background: tokens.color.surface[2],
          color: tokens.color.ink.primary,
          cursor: readOnly ? 'not-allowed' : 'pointer',
        }}
      >
        <img
          src={lineSymbolSvgDataUrl(value, compact ? 48 : 72)}
          width={compact ? 48 : 72}
          height={30}
          alt=""
          aria-hidden="true"
        />
        <Icon name={open ? 'expand_less' : 'expand_more'} size={20} decorative />
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Line symbol gallery"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: tokens.zIndex.tooltip,
            display: 'grid',
            gridTemplateColumns: `repeat(2, minmax(${tokens.layout.popoverXs / 2}px, 1fr))`,
            gap: tokens.space[1],
            width: tokens.layout.popoverMd,
            padding: tokens.space[2],
            border: tokens.border.thin,
            borderRadius: tokens.radius.md,
            background: tokens.color.surface[1],
            boxShadow: tokens.elevation[2],
          }}
        >
          {LINE_SYMBOL_PRESETS.map((preset) => {
            const selected = preset.id === value.presetId;
            const preview = applyLineSymbolPreset(value, preset.id);
            return (
              <button
                key={preset.id}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={preset.label}
                title={preset.description}
                disabled={readOnly}
                onClick={() => {
                  onChange(preview);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: tokens.space[1],
                  minWidth: 0,
                  padding: tokens.space[1],
                  border: selected
                    ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
                    : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                  borderRadius: tokens.radius.sm,
                  background: selected ? tokens.color.surface[3] : tokens.color.surface[2],
                  color: tokens.color.ink.primary,
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                }}
              >
                <img
                  src={lineSymbolSvgDataUrl(preview, 96)}
                  width={96}
                  height={30}
                  alt=""
                  aria-hidden="true"
                  style={{ maxWidth: '100%' }}
                />
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: tokens.font.size.xs,
                  }}
                >
                  {preset.label}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
